import type { ReactNode } from "react";
import Link from "next/link";
import { AppHeader } from "@/components/layout/AppHeader";
import { FreeToolsNav, type FreeToolKey } from "./FreeToolsNav";
import { ReadingZone } from "./ReadingZone";
import { HideForSubscribers } from "./HideForSubscribers";

const LEGAL_LINKS = [
  { href: "/guides", label: "Guides" },
  { href: "/privacy-policy", label: "Privacy" },
  { href: "/terms", label: "Terms" },
];

/**
 * Server-rendered chrome shared by every `/free/**` page: the same `AppHeader`
 * every authed and marketing page uses, a constrained content column, the
 * cross-link "more free tools" grid, a global conversion CTA, and a
 * lightweight footer. This component itself has no client-only dependencies,
 * so the page body still prerenders as static HTML for SEO — `AppHeader` is a
 * `"use client"` component, so it becomes its own client island inside an
 * otherwise server-rendered tree rather than dragging the whole shell (or the
 * page) into client rendering. See "WHY APPHEADER AND NOT A BESPOKE SLIM
 * HEADER" below for why that swap is safe for prerendering.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY APPHEADER AND NOT A BESPOKE SLIM HEADER
 *
 * This used to render its own two-link header (a "back to The Workshop"
 * chevron and a "Free tools" pill) specifically so the page had zero
 * client-only dependencies. That produced a page whose header looked like no
 * other page in the app and offered a signed-out visitor no sign-in/pricing
 * affordance and a subscriber no way back into the Hub — a free tool was a
 * dead end for exactly the two audiences most worth converting.
 *
 * `AppHeader` already branches on signed-out / signed-in-free / subscriber /
 * admin, so swapping it in gets all of that for free, and it does not cost the
 * static prerender: `AppHeader` is `"use client"` but performs no dynamic
 * server read (no `cookies()`/`headers()`) — that's exactly the property
 * `app/(public)/guides/page.tsx` and `src/views/Landing.tsx` already rely on,
 * both of which render `AppHeader` (via `GuidesView`, and indirectly via
 * `HomePageSwitcher`) and remain statically generated. `AppHeader` reads auth
 * state from `AuthProvider`'s React context (mounted at the root layout, so
 * every `/free/**` page already sits inside it) and resolves everything
 * client-side after hydration; the prerendered HTML gets its signed-out
 * default, same as any other client-hydrated widget on this site.
 *
 * `AppHeader` is rendered with no `back` link: the owner wants the same
 * header every other page in the app gets, full stop, not a free-tools-only
 * variant with its own sub-row. The way back to `/free` is `FreeToolsNav`'s
 * "All free tools" link, already rendered a few hundred pixels below.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY ONE SHELL WITH A WIDTH PROP AND NOT TWO SHELLS
 *
 * The column this started as — 48rem of prose — is the right column for the two
 * tools it was written for. JOAAT takes one string and returns three numbers;
 * the coordinates helper takes six and returns four lines. Both are reading
 * pages with a small input on them, and a reading page that runs wider than
 * about 75 characters is measurably harder to read.
 *
 * Four of the six free tools in the server-owner plan are not that. A clash
 * report over a dropped `resources/` folder is a table of several hundred rows
 * whose leftmost column is a full resource-relative path, and 48rem either
 * truncates the path — which is the one cell the reader needs, because the path
 * *is* the answer — or wraps every row to three lines and turns three hundred
 * rows into a thousand. So those pages need a wider column.
 *
 * The obvious move is a second shell, and it is the wrong one. The header, the
 * `FreeToolsNav` grid, the conversion CTA and the footer are not per-tool
 * decisions; they are the site. A `WideToolShell` copied from this file starts
 * identical and is wrong within a month, because the next person to add a link
 * to the footer or a line to the CTA edits one of the two files and ships a
 * public page that disagrees with its neighbours about what The Workshop sells.
 * One shell, one prop, one copy of the chrome.
 *
 * WHY CHROME AND CONTENT ARE LOOKED UP TOGETHER
 *
 * The header and the footer sit at `max-w-5xl` while `main` sits at
 * `max-w-3xl`. That mismatch is deliberate at prose width — the back link and
 * the legal row are meant to sit outboard of the reading column — and because
 * both are `mx-auto` and the chrome is the wider of the two, the content stays
 * visually nested inside it.
 *
 * That relationship breaks the instant only the content grows: a 7xl table
 * under a 5xl header runs out from under its own chrome, so the page's left
 * edge is in one place for the table and 8rem inboard for the "The Workshop"
 * link above it. The failure is invisible on a laptop with a narrow viewport,
 * because at that point both are clamped by the screen and the bug only appears
 * on the wide monitors the wide pages exist for.
 *
 * So the two widths are one decision, made once, in `WIDTHS` below, and the
 * header, `main` and the footer all read from the same record. The invariant
 * the record has to keep is that chrome is never narrower than content.
 *
 * WHY THERE IS A SEPARATE SLOT FOR THE READING ZONE
 *
 * `width` is about the tool. Reading measure is a third number, and until the
 * `reading` prop existed it was a per-page decision that three pages made by
 * hand-writing `max-w-3xl` on their `<article>` and one page forgot — which was
 * a defect, not a variation: `/free/server-cfg`'s shell width is decided by its
 * island, so dropping a `resources/` folder reflowed 1,765 words of body copy
 * from 48rem to 80rem mid-session.
 *
 * A node passed as `reading` is rendered after `children`, behind the
 * tool/reading boundary, inside a `mx-auto max-w-3xl` column that no `width`
 * can stretch. That is the same argument this file already makes about the
 * header and the footer, applied to the one measurement it had left out: the
 * prose column is not a per-tool decision either. It also means a wide tool and
 * a 75-character reading measure stop being in conflict — `main` can be 80rem
 * for a six-column findings table while the essay under it stays at 48rem.
 *
 * `reading` is optional because a page with no SEO `<article>` and no FAQ
 * has nothing to put in it, and rendering a "Reference" boundary above
 * nothing would be a label for an empty room.
 *
 * LIMITS
 *
 * This is a page frame and nothing else. It gives a wide tool room for a table;
 * it does not make the table responsive. A caller rendering many columns still
 * owns its own horizontal `overflow-x-auto` wrapper and its own behaviour at
 * phone width, because only the caller knows which of its columns may be
 * dropped and which one is the answer.
 *
 * Also note, and this is pre-existing rather than introduced here: the footer's
 * `new Date().getFullYear()` runs in a server component with no dynamic API on
 * the page, so it is evaluated at build time and the copyright year is pinned
 * to whenever the site was last deployed.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** Which column the page wants. `prose` is the reading column these pages started as. */
export type FreeToolWidth = "prose" | "wide";

/**
 * Content column and the header/footer chrome that has to frame it, paired so
 * they cannot drift apart.
 *
 * `prose` reproduces the pre-prop markup exactly — 3xl content inside 5xl
 * chrome — so the three pages already shipping on this shell render the same
 * bytes they rendered before the prop existed.
 *
 * `wide` is `max-w-7xl`, 80rem, for three reasons. It fits a six-column findings
 * table — resource, path, size, kind, conflicting-with, severity — at a
 * readable size without truncating the path cell, which is the whole output.
 * It is already the house wide-page width (`Admin`, `Account`, `AssetDirectory`,
 * `AddonEupCreator` all use it), so a user moving between a free tool and the
 * app does not meet a third measurement. And it stops short of full bleed on a
 * 27" monitor, where a row 2,000px wide makes the severity badge on the right
 * impossible to associate by eye with the resource name on the left.
 *
 * At `wide` the chrome matches the content exactly rather than sitting outboard
 * of it: the alternative is chrome wider than 7xl, which is the full-bleed
 * problem again, this time applied to a back link and three legal links.
 */
const WIDTHS: Record<FreeToolWidth, { chrome: string; content: string }> = {
  prose: { chrome: "max-w-5xl", content: "max-w-3xl" },
  wide: { chrome: "max-w-7xl", content: "max-w-7xl" },
};

export function FreeToolShell({
  current,
  width = "prose",
  reading,
  children,
}: {
  current?: FreeToolKey;
  /** `prose` (default) for reading pages, `wide` for a page that renders a table. */
  width?: FreeToolWidth;
  /**
   * The page's SEO `<article>`. Rendered after `children`, behind the
   * tool/reading boundary and clamped to a 48rem reading column whatever
   * `width` says. Omit it on a page that has no reference prose.
   */
  reading?: ReactNode;
  children: ReactNode;
}) {
  const { chrome, content } = WIDTHS[width];
  return (
    <div className="min-h-screen bg-background text-foreground">
      <AppHeader />

      <main className={`mx-auto ${content} px-4 pb-6 pt-10 sm:px-6`}>
        {children}

        {/*
          The reading zone, then the cross-link grid, then the CTA — and in that
          order on purpose. Moving the six sibling-tool cards up under the
          boundary was considered and rejected: it wedges a card grid between
          the "Reference" label and the prose it labels, which destroys the one
          edge this whole arrangement exists to draw. A reader who has finished
          with the tool has the header's jump row and the table of contents
          instead.
        */}
        {reading ? <ReadingZone>{reading}</ReadingZone> : null}

        {/* Cross-links to the other free tools — a subscriber has them all in
            their own hub grid already. */}
        <HideForSubscribers>
          <FreeToolsNav current={current} />
        </HideForSubscribers>

        {/* Global conversion CTA — not shown to a subscriber, who already has it all. */}
        <HideForSubscribers>
          <section className="mt-12 overflow-hidden rounded-2xl border border-primary/25 bg-primary/8 p-6 sm:p-8">
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              Build the whole thing in your browser
            </h2>
            <p className="mt-2 max-w-xl text-sm leading-7 text-muted-foreground">
              These free tools are a taste. The Workshop gives you 15+ FiveM creation tools in one
              subscription — Paint Booth, UV Studio, EUP Viewer, Easy Meta, PackBench and more — no
              installs, no downloads, right in the browser.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link
                href="/"
                className="inline-flex items-center rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
              >
                See all tools
              </Link>
              <Link
                href="/guides"
                className="inline-flex items-center rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:border-primary/30 hover:bg-muted/40"
              >
                Read the guides
              </Link>
            </div>
          </section>
        </HideForSubscribers>
      </main>

      <footer className="border-t border-border/50">
        <div
          className={`mx-auto flex ${chrome} flex-col items-center justify-between gap-3 px-4 py-6 text-xs text-muted-foreground sm:flex-row sm:px-6`}
        >
          <span>© {new Date().getFullYear()} The Workshop</span>
          <nav className="flex items-center gap-4">
            {LEGAL_LINKS.map((l) => (
              <Link key={l.href} href={l.href} className="transition-colors hover:text-foreground">
                {l.label}
              </Link>
            ))}
          </nav>
        </div>
      </footer>
    </div>
  );
}
